import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HttpStatusCode } from "axios";

import { formatPostTitle } from "../../../libs/talk";
import {
  addLocalArrayValue,
  getLocalArrayValue,
} from "../../../libs/localStorage";

import { StoreItemType } from "../../../types/talk";

import { TalkListItem } from "../../../services/talkServices";
import {
  getSpaceReviewSummary,
  SpaceReviewSummaryResponse,
} from "../../../services/spaceServices";

import { useSandiTalk } from "../../../contexts/boardContext";

import { PostFooter } from "../../../pages/Talk/Detail";
import HorizontalRule from "../../Rule/Horizontal";
import UserProfile from "../../UserProfile";
import CommonSelect, { SelectOption } from "../../Select";
import StarScore from "../StarScore";

type Props = {
  postList: TalkListItem[];
  onClickLike?: (id: number) => void;
  unUseHeader?: boolean;
  storeList?: StoreItemType[];
  emptyMessage?: string;
  parentUrl?: string;
  isFaq?: boolean;
};

export default function PostList({
  postList,
  onClickLike,
  unUseHeader,
  storeList,
  emptyMessage,
  parentUrl,
  isFaq = false,
}: Props) {
  const outerLinkBuilding = ["TG_0001813"];
  const outerLinkStoreUrl = "https://greeat.52g.gs";

  const navigate = useNavigate();
  const [talk, { updateField }] = useSandiTalk();
  const sortOption: SelectOption[] = [
    { label: "등록일순", value: "createdAt.desc" },
    { label: "댓글 등록순", value: "commentCreatedAt.desc" },
    { label: "좋아요순", value: "likeCount.desc" },
    { label: "댓글순", value: "commentCount.desc" },
  ];
  const [selectedStore, setSelectedStore] =
    React.useState<StoreItemType | null>(null);
  const [reviewSummary, setReviewSummary] =
    useState<SpaceReviewSummaryResponse>();


  useEffect(() => {
    if (selectedStore) {
      getSpaceReviewSummary({ spaceId: selectedStore.storeId }).then((res) => {
        if (res.status === HttpStatusCode.Ok) {
          setReviewSummary(res.data);
        }
      });
    }
  }, [selectedStore]);

  useEffect(() => {
    const prev = getLocalArrayValue(
      "talkMenuStore",
      "buildingId",
      talk?.buildingId
    );
    const targetStore = storeList?.find(
      (store) => store?.storeId === prev?.storeId
    );

    setSelectedStore(targetStore ?? (storeList ? storeList[0] : null));
    if (storeList && storeList.length > 0) {
      updateField({
        storeId: targetStore?.storeId ?? storeList[0].storeId.toString(),
      });
    }
  }, [storeList]);

  useEffect(() => {
    updateField({
      sort: sortOption[0].value,
    });
  }, []);

  return (
    <>
      {!unUseHeader && (
        <div className="px-5 flex justify-between w-full py-2 flex-wrap">
          <div className="self-end">
            {storeList && storeList?.length > 0 && (
              <CommonSelect
                value={selectedStore?.storeId.toString() ?? ""}
                optionList={
                  storeList?.map(
                    (store) =>
                      ({
                        value: store.storeId.toString(),
                        label: store.name,
                      } as SelectOption)
                  ) ?? []
                }
                onChange={(option) => {
                  setSelectedStore({
                    storeId: option.value,
                    name: option.label,
                  } as StoreItemType);
                  updateField({ storeId: option.value });
                  addLocalArrayValue("talkMenuStore", "buildingId", {
                    buildingId: talk.buildingId,
                    storeId: option.value,
                  });
                }}
                labelClassName="min-w-[calc(100vw-250px)]"
                className="text-sm font-semibold"
                optionOrder="left"
                variant="storeMenu"
                useTruncate
              />
            )}
          </div>

          {!isFaq && (
            <div className="flex justify-end">
              <CommonSelect
                value={talk.sort ?? sortOption[0].value}
                optionList={sortOption}
              onChange={(option) => {
                updateField({ sort: option.value });
              }}
              className="border-none w-[110px] text-right"
              optionOrder="right"
              variant="transparent"
              />
            </div>
          )}
        </div>
      )}
      {storeList && storeList.length > 0 && (
        <div className="px-5">
          <div
            className=" bg-brand-gray-140 rounded-[10px] flex justify-between items-center p-5"
            onClick={() => {
              if (
                talk.buildingId &&
                outerLinkBuilding.includes(talk.buildingId)
              ) {
                window.open(outerLinkStoreUrl); // #1364: GS타워 건물의 경우 외부링크 적용
              } else {
                navigate(`/talk/store/${selectedStore?.storeId}`);
              }
            }}
          >
            <div className="text-white">
              <div className="flex items-center gap-1">
                <span className=" text-3xl font-semibold text-brand-primary-magenta">
                  {reviewSummary?.rating ?? 0}
                </span>{" "}
                / 5.0
              </div>
              <div className="text-sm flex gap-2">
                <StarScore score={reviewSummary?.rating ?? 0} />
                {reviewSummary?.countReview ?? 0}건
              </div>
            </div>
            <div>
              <StoreRightArrowIcon />
            </div>
          </div>
        </div>
      )}
      <div className="flex flex-col gap-4 px-5 pb-32">
        {postList && postList.length > 0 ? (
          postList?.map((post) => (
            <PostListItem
              key={post.lifePostId}
              post={post}
              onClickLike={onClickLike}
              parentUrl={parentUrl}
              isFaq={isFaq}
            />
          ))
        ) : (
          <div className="flex flex-col  items-center justify-center w-full h-40 gap-3 text-brand-neutral-60">
            <PostListNoPostIcon />
            {emptyMessage ?? "게시글이 없어요"}
          </div>
        )}
      </div>
    </>
  );
}

export const PostListItem = ({
  post,
  onClickLike,
  parentUrl,
  isFaq = false,
}: {
  post: TalkListItem;
  onClickLike?: (id: number) => void;
  parentUrl?: string;
  isFaq?: boolean;
}) => {
  const navigate = useNavigate();
  const fileList = post.fileInfo?.items?.filter(
    (item) => item.scale === "small"
  );
  const plainContent =
    new DOMParser().parseFromString(post.content, "text/html").body
      .textContent || "";

  const shortContent = plainContent
    .split("\n")
    .slice(0, 3)
    .join("\n")
    .slice(0, 80);

  return (
    <div
      key={post.lifePostId}
      className="rounded-xl p-5 flex flex-col gap-4"
      onClick={() => {
        if(!isFaq ) {
          navigate(`/talk/${post.lifePostId}`, { state: { parentUrl } }) 
        } else {
          navigate(`/myinfo/faq/${post.lifePostId}`)
        }
      }
      }
      style={{ boxShadow: "0px 1px 5px 0px rgba(0, 0, 0, 0.08)" }}
    >
      <UserProfile
        nickname={post.nickname ?? ""}
        createdAt={post.createdAt ?? ""}
        modifiedAt={post.modifiedAt ?? ""}
        profile={
          post.memberFileInfo?.items.find((item) => item.scale === "small")?.url
        }
        role={post?.userRole}
        isFaq={isFaq}
      />
      <HorizontalRule margin="0" className="bg-brand-neutral-05" />
      <div className="flex gap-2.5">
        <div className="flex flex-col gap-2.5 flex-1">
          {formatPostTitle(post.title) && (
            <div className=" text-base font-medium">
              {formatPostTitle(post.title)}
            </div>
          )}
          <div className=" text-xs text-brand-neutral-40 whitespace-pre-line">
            {shortContent}
            {plainContent === shortContent ? "" : "..."}
          </div>
        </div>
        {fileList && fileList.length > 0 && (
          <div className="h-[80px] w-[80px] relative">
            <div className="h-[80px] w-[80px] flex items-center justify-center">
              <img
                src={fileList[0].url}
                alt="img"
                className="object-cover h-[80px] w-[80px]"
              />
            </div>
            {fileList.length > 1 && (
              <div className="absolute bottom-0 right-0 px-[5px] py-1 text-[10px] font-semibold bg-brand-primary-gray-100 bg-opacity-40 text-white gap-[5px] flex items-center">
                <span>{fileList.length - 1}</span>
                <span>+</span>
              </div>
            )}
          </div>
        )}
      </div>
      <PostFooter
        post={post}
        onClickLike={onClickLike}
        type="post"
        unUseReport
        isFaq={isFaq}
      />
    </div>
  );
};

export const PostListNoPostIcon = () => (
  <svg
    width="60"
    height="61"
    viewBox="0 0 60 61"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M55.3994 55.8997L41.9803 42.4805M26.828 48.7568C14.9933 48.7568 5.39941 39.1629 5.39941 27.3283C5.39941 15.4936 14.9933 5.89969 26.828 5.89969C38.6627 5.89969 48.2566 15.4936 48.2566 27.3283C48.2566 39.1629 38.6627 48.7568 26.828 48.7568Z"
      stroke="black"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path
      d="M42.6981 23.9039C48.6086 23.9039 53.4 19.1125 53.4 13.202C53.4 7.29143 48.6086 2.5 42.6981 2.5C36.7875 2.5 31.9961 7.29143 31.9961 13.202C31.9961 19.1125 36.7875 23.9039 42.6981 23.9039Z"
      fill="black"
      stroke="white"
      strokeWidth="4"
    />
    <path
      d="M45.9508 9.94922L39.4453 16.4547"
      stroke="white"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
    <path
      d="M39.4453 9.94922L45.9508 16.4547"
      stroke="white"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
  </svg>
);

export const StoreRightArrowIcon = () => (
  <svg
    width="24"
    height="25"
    viewBox="0 0 24 25"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9 18.5L15 12.5L9 6.5"
      stroke="white"
      strokeWidth="1.75"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
  </svg>
);
